// src/pages/admin/AdminPaymentsManagement.jsx — v1.394.0
// v1.394.0 — Colonne Client : plus de « N/A » quand un téléphone existe,
// téléphone formaté (+212 6 81 37 93 26) et cliquable.
// v1.393.0 — FIX calculs : liste paginée et filtrée CÔTÉ SERVEUR (avant : seuls
// les 20 derniers paiements étaient chargés), stats (« Total Reçu », « Ce Mois »,
// compteurs) recalculées avec les MÊMES filtres (méthode / recherche / période).
// AbodyStudio Limited — https://abodystudio.com/ — Support@abodystudio.com
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import API from '../../services/api';
import { toast } from 'react-toastify';
import { FiChevronLeft, FiChevronRight } from 'react-icons/fi';

const AdminPaymentsManagement = () => {
  const [payments, setPayments] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [methodFilter, setMethodFilter] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const mountedRef = useRef(true);
  const reqIdRef = useRef(0);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Recherche : on attend 350 ms après la dernière frappe
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchTerm.trim()), 350);
    return () => clearTimeout(t);
  }, [searchTerm]);

  // Filtres communs liste + stats
  const filterParams = useMemo(() => {
    const p = {};
    if (methodFilter !== 'all') p.method = methodFilter;
    if (debouncedSearch) p.search = debouncedSearch;
    if (startDate) p.startDate = startDate;
    if (endDate) p.endDate = endDate;
    return p;
  }, [methodFilter, debouncedSearch, startDate, endDate]);

  const fetchPayments = useCallback(async () => {
    const reqId = ++reqIdRef.current;
    if (mountedRef.current) setRefreshing(true);
    try {
      const params = { ...filterParams, page, limit };
      if (statusFilter !== 'all') params.status = statusFilter;
      const res = await API.get('/admin/payments', { params });
      if (!mountedRef.current || reqId !== reqIdRef.current) return;
      const list = res?.data?.success ? res.data.data || [] : [];
      const pg = res?.data?.pagination || {};
      setPayments(Array.isArray(list) ? list : []);
      setTotalItems(Number(pg.total) || 0);
      setTotalPages(Math.max(1, Number(pg.pages) || 1));
    } catch (e) {
      if (reqId !== reqIdRef.current) return;
      toast.error('Erreur chargement paiements');
      if (mountedRef.current) setPayments([]);
    } finally {
      if (mountedRef.current && reqId === reqIdRef.current) {
        setLoading(false);
        setRefreshing(false);
      }
    }
  }, [filterParams, statusFilter, page, limit]);

  const fetchStats = useCallback(async () => {
    try {
      const res = await API.get('/admin/payments/stats', { params: filterParams });
      if (res?.data?.success && res.data.data && mountedRef.current) {
        setStats(res.data.data);
      }
    } catch (e) {}
  }, [filterParams]);

  useEffect(() => {
    fetchPayments();
  }, [fetchPayments]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const safeLower = (v) => String(v ?? '').toLowerCase();
  const safeStr = (v) => String(v ?? '').trim();

  const normalize = (v) =>
    safeStr(v)
      .toLowerCase()
      .replace(/\s+/g, ' ')
      .replace(/[^\p{L}\p{N}\s]/gu, '');


  // 🔑 provider = cmi | payzone
  const getProvider = (p) => safeLower(p?.provider || p?.paymentProvider || '');

  const getRawMethod = (p) =>
    safeLower(p?.method || p?.paymentMethod || p?.orderId?.paymentMethod).trim();

  const classifyMethod = (p) => {
    const key = getRawMethod(p);
    const provider = getProvider(p);

    const isCmi = provider === 'cmi';

    const isCard =
      key === 'card' ||
      key === 'carte' ||
      key.includes('visa') ||
      key.includes('master') ||
      key.includes('card') ||
      provider === 'payzone'; // ✅ Payzone = carte bancaire

    const isCash =
      key === 'cash' ||
      key === 'espece' ||
      key === 'espèce' ||
      key === 'especes' ||
      key === 'espèces';

    return { key, isCmi, isCard, isCash, provider };
  };

  // Téléphone lisible : 212681379326 / 0681379326 -> +212 6 81 37 93 26
  const formatPhone = (raw) => {
    const str = safeStr(raw);
    if (!str) return { label: '', href: '' };
    let d = str.replace(/[^\d]/g, '');
    if (str.startsWith('00')) d = d.slice(2);
    if (d.length === 10 && d.startsWith('0')) d = '212' + d.slice(1);
    if (d.length === 9 && /^[5-7]/.test(d)) d = '212' + d;
    if (d.startsWith('212') && d.length === 12) {
      const n = d.slice(3);
      return {
        label: `+212 ${n[0]} ${n.slice(1, 3)} ${n.slice(3, 5)} ${n.slice(5, 7)} ${n.slice(7, 9)}`,
        href: `tel:+${d}`
      };
    }
    if (d.startsWith('33') && d.length === 11) {
      const n = d.slice(2);
      return {
        label: `+33 ${n[0]} ${n.slice(1, 3)} ${n.slice(3, 5)} ${n.slice(5, 7)} ${n.slice(7, 9)}`,
        href: `tel:+${d}`
      };
    }
    if (d.length >= 8) return { label: `+${d}`, href: `tel:+${d}` };
    return { label: str, href: '' };
  };

  const getUserName = (p) => {
    const u = p?.userId || {};
    const full = `${u?.firstName || ''} ${u?.lastName || ''}`.trim();
    return full || u?.name || p?.orderId?.guestName || u?.email || 'Client';
  };

  const getCmiName = (p) =>
    safeStr(
      p?.cmiResponse?.BillToName ||
        p?.paymentData?.BillToName ||
        p?.cmiResponse?.billToName ||
        p?.paymentData?.billToName ||
        ''
    );

  const getCmiEmail = (p) =>
    safeStr(
      p?.cmiResponse?.email ||
        p?.paymentData?.email ||
        p?.cmiResponse?.Email ||
        p?.paymentData?.Email ||
        ''
    );

  const getCmiPhone = (p) =>
    safeStr(
      p?.cmiResponse?.tel ||
        p?.cmiResponse?.phone ||
        p?.paymentData?.tel ||
        p?.paymentData?.phone ||
        p?.cmiResponse?.BillToTel ||
        p?.paymentData?.BillToTel ||
        ''
    );

  const isDifferentName = (maxulaName, cmiName) => {
    const a = normalize(maxulaName);
    const b = normalize(cmiName);
    if (!a || !b) return false;
    if (a.includes(b) || b.includes(a)) return false;
    return a !== b;
  };

  const getStatusBadge = (statusRaw) => {
    const status = safeLower(statusRaw).trim();
    const styles = {
      completed: 'bg-green-100 text-green-800',
      pending: 'bg-yellow-100 text-yellow-800',
      failed: 'bg-red-100 text-red-800',
      refunded: 'bg-purple-100 text-purple-800',
      refund_pending: 'bg-orange-100 text-orange-800',
      cancelled: 'bg-gray-100 text-gray-800'
    };
    const labels = {
      completed: 'Complété',
      pending: 'En attente',
      failed: 'Échoué',
      refunded: 'Remboursé',
      refund_pending: 'Remb. en cours',
      cancelled: 'Annulé'
    };
    return (
      <span
        className={`px-2 py-1 rounded-full text-xs font-semibold ${
          styles[status] || 'bg-gray-100 text-gray-800'
        }`}
      >
        {labels[status] || status || 'Inconnu'}
      </span>
    );
  };

  const getMethodLabel = (p) => {
    const { isCmi, isCard, isCash, provider, key } = classifyMethod(p);
    if (isCmi) return 'CMI';
    if (provider === 'payzone') return 'Payzone (Carte bancaire)';
    if (isCard) return 'Carte bancaire';
    if (isCash) return 'Espèces';
    const extra = { virement: 'Virement', tpe: 'TPE', payexpress: 'PayExpress', wallet: 'Portefeuille' };
    return extra[key] || key || 'Inconnu';
  };

  const paginatedPayments = payments;

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, statusFilter, methodFilter, startDate, endDate, limit]);

  const goToPage = (p) => {
    const next = Math.max(1, Math.min(totalPages, p));
    if (next !== page) setPage(next);
  };

  const fmtMAD = (v) =>
    `${Number(v || 0).toLocaleString('fr-FR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })} MAD`;

  const METHOD_LABELS = {
    cmi: 'CMI',
    card: 'Carte bancaire',
    cash: 'Espèces',
    virement: 'Virement',
    tpe: 'TPE',
    payexpress: 'PayExpress',
    wallet: 'Portefeuille'
  };

  const hasFilters =
    methodFilter !== 'all' || !!debouncedSearch || !!startDate || !!endDate;
  const filterLabel = [
    methodFilter !== 'all' ? METHOD_LABELS[methodFilter] : null,
    debouncedSearch ? `« ${debouncedSearch} »` : null,
    startDate || endDate ? `${startDate || '…'} → ${endDate || '…'}` : null
  ]
    .filter(Boolean)
    .join(' · ');

  const resetFilters = () => {
    setSearchTerm('');
    setDebouncedSearch('');
    setStatusFilter('all');
    setMethodFilter('all');
    setStartDate('');
    setEndDate('');
  };

  const displayStats = {
    revenue: { total: 0, month: 0, year: 0, today: 0, ...(stats?.revenue || {}) },
    amounts: { pending: 0, refunded: 0, ...(stats?.amounts || {}) },
    counts: {
      completed: 0, completedMonth: 0, pending: 0, failed: 0, cancelled: 0,
      refunded: 0, refund_pending: 0, total: 0, ...(stats?.counts || {})
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin h-10 w-10 border-b-2 border-primary-600 rounded-full" />
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Gestion des Paiements</h1>
        <p className="text-gray-600 mt-1">Suivez toutes les transactions</p>
      </div>

      {hasFilters && (
        <div className="mb-3 flex flex-wrap items-center gap-2 text-sm">
          <span className="bg-primary-50 text-primary-700 border border-primary-200 px-3 py-1 rounded-full">
            Chiffres filtrés : <strong>{filterLabel}</strong>
          </span>
          <button
            type="button"
            onClick={resetFilters}
            className="text-gray-500 hover:text-gray-800 underline"
          >
            Réinitialiser
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Stat
          label="Total Reçu"
          value={fmtMAD(displayStats.revenue.total)}
          hint={`${displayStats.counts.completed} paiement(s) complété(s)`}
          color="green"
        />
        <Stat
          label="Ce Mois"
          value={fmtMAD(displayStats.revenue.month)}
          hint={`Aujourd'hui : ${fmtMAD(displayStats.revenue.today)}`}
          color="blue"
        />
        <Stat
          label="Complétés"
          value={Number(displayStats.counts.completed || 0)}
          hint={`${displayStats.counts.completedMonth} ce mois`}
          color="green"
        />
        <Stat
          label="En Attente"
          value={Number(displayStats.counts.pending || 0)}
          hint={fmtMAD(displayStats.amounts.pending)}
          color="yellow"
        />
      </div>

      {(displayStats.counts.refunded > 0 ||
        displayStats.counts.refund_pending > 0 ||
        displayStats.counts.cancelled > 0 ||
        displayStats.counts.failed > 0) && (
        <div className="mb-6 text-xs text-gray-500 flex flex-wrap gap-x-4 gap-y-1">
          <span>Exclus du total :</span>
          {displayStats.counts.refunded > 0 && (
            <span>{displayStats.counts.refunded} remboursé(s)</span>
          )}
          {displayStats.counts.refund_pending > 0 && (
            <span>{displayStats.counts.refund_pending} remboursement(s) en cours</span>
          )}
          {displayStats.amounts.refunded > 0 && (
            <span>({fmtMAD(displayStats.amounts.refunded)} rendus)</span>
          )}
          {displayStats.counts.cancelled > 0 && (
            <span>{displayStats.counts.cancelled} annulé(s)</span>
          )}
          {displayStats.counts.failed > 0 && (
            <span>{displayStats.counts.failed} échoué(s)</span>
          )}
        </div>
      )}

      <div className="bg-white p-4 rounded shadow mb-6 grid md:grid-cols-6 gap-4">
        <input
          className="border rounded px-3 py-2 md:col-span-2"
          placeholder="Transaction, commande, email, nom, téléphone..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />

        <select
          className="border rounded px-3 py-2"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="all">Tous les statuts</option>
          <option value="completed">Complétés</option>
          <option value="pending">En attente</option>
          <option value="failed">Échoués</option>
          <option value="cancelled">Annulés</option>
          <option value="refund_pending">Remboursement en cours</option>
          <option value="refunded">Remboursés</option>
        </select>

        <select
          className="border rounded px-3 py-2"
          value={methodFilter}
          onChange={(e) => setMethodFilter(e.target.value)}
        >
          <option value="all">Toutes les méthodes</option>
          {Object.entries(METHOD_LABELS).map(([k, v]) => (
            <option key={k} value={k}>
              {v}
            </option>
          ))}
        </select>

        <input
          type="date"
          className="border rounded px-3 py-2"
          value={startDate}
          max={endDate || undefined}
          onChange={(e) => setStartDate(e.target.value)}
          title="Du"
        />
        <input
          type="date"
          className="border rounded px-3 py-2"
          value={endDate}
          min={startDate || undefined}
          onChange={(e) => setEndDate(e.target.value)}
          title="Au"
        />
      </div>

      <div className={`bg-white rounded shadow overflow-hidden transition-opacity ${refreshing ? 'opacity-60' : ''}`}>
        <table className="w-full">
          <thead className="bg-gray-50 text-xs uppercase">
            <tr>
              <th className="p-3 text-left">Transaction</th>
              <th className="p-3 text-left">Commande</th>
              <th className="p-3 text-left">Client</th>
              <th className="p-3 text-left">Montant</th>
              <th className="p-3 text-left">Méthode</th>
              <th className="p-3 text-left">Statut</th>
              <th className="p-3 text-left">Date</th>
            </tr>
          </thead>
          <tbody>
            {paginatedPayments.length > 0 ? (
              paginatedPayments.map((p) => {
                const txId =
                  p?.transactionId || (p?._id ? String(p._id).slice(-8) : 'N/A');
                const orderNumber =
                  p?.orderNumber || p?.orderId?.orderNumber || '-';
                const maxulaName = getUserName(p);
                const maxulaEmail = p?.userId?.email || p?.orderId?.guestEmail || '';
                const maxulaPhone = p?.userId?.phone || p?.orderId?.guestPhone || '';

                const { isCmi } = classifyMethod(p);

                const cmiName = getCmiName(p);
                const cmiEmail = getCmiEmail(p);
                const cmiPhone = getCmiPhone(p);

                const diffName =
                  isCmi && cmiName && isDifferentName(maxulaName, cmiName);

                const created = p?.createdAt ? new Date(p.createdAt) : null;

                return (
                  <tr key={p._id || txId} className="border-t hover:bg-gray-50">
                    <td className="p-3 font-mono text-sm">#{txId}</td>
                    <td className="p-3 text-sm text-gray-700">{orderNumber}</td>
                    <td className="p-3 text-sm text-gray-700">
                      <div className="font-semibold text-gray-900">
                        {maxulaName}
                      </div>
                      <div className="text-xs text-gray-500">
                        {(() => {
                          const ph = formatPhone(maxulaPhone);
                          if (!maxulaEmail && !ph.label) return '—';
                          return (
                            <>
                              {maxulaEmail}
                              {maxulaEmail && ph.label ? ' • ' : ''}
                              {ph.label &&
                                (ph.href ? (
                                  <a href={ph.href} className="hover:text-primary-600 whitespace-nowrap">
                                    {ph.label}
                                  </a>
                                ) : (
                                  <span className="whitespace-nowrap">{ph.label}</span>
                                ))}
                            </>
                          );
                        })()}
                      </div>

                      {isCmi && (cmiName || cmiEmail || cmiPhone) && (
                        <div className="mt-2 bg-gray-50 border rounded p-2 text-xs">
                          <div className="font-semibold flex items-center gap-2">
                            <span>Infos CMI</span>
                            {diffName && (
                              <span className="bg-orange-100 text-orange-800 px-2 py-0.5 rounded-full font-semibold">
                                Nom carte ≠ compte
                              </span>
                            )}
                          </div>
                          {cmiName && (
                            <div className="mt-1">
                              Nom carte:{' '}
                              <span className="font-medium">{cmiName}</span>
                            </div>
                          )}
                          {cmiEmail && (
                            <div className="mt-1">
                              Email CMI:{' '}
                              <span className="font-medium">{cmiEmail}</span>
                            </div>
                          )}
                          {cmiPhone && (
                            <div className="mt-1">
                              Tél CMI:{' '}
                              <span className="font-medium">{cmiPhone}</span>
                            </div>
                          )}
                        </div>
                      )}
                    </td>
                    <td className="p-3 text-sm font-semibold text-gray-900">
                      {fmtMAD(p?.amount)}
                    </td>
                    <td className="p-3 text-sm text-gray-700">
                      {getMethodLabel(p)}
                    </td>
                    <td className="p-3">{getStatusBadge(p?.status)}</td>
                    <td className="p-3 text-sm text-gray-500">
                      {created ? created.toLocaleDateString('fr-FR') : '-'}
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan="7" className="p-10 text-center text-gray-500">
                  Aucun paiement trouvé
                </td>
              </tr>
            )}
          </tbody>
        </table>

        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 p-4 border-t">
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => goToPage(page - 1)}
              disabled={page <= 1}
              className="border px-3 py-2 rounded disabled:opacity-50 flex items-center gap-1"
            >
              <FiChevronLeft /> <span>Préc</span>
            </button>
            <button
              type="button"
              onClick={() => goToPage(page + 1)}
              disabled={page >= totalPages}
              className="border px-3 py-2 rounded disabled:opacity-50 flex items-center gap-1"
            >
              <span>Suiv</span> <FiChevronRight />
            </button>
            <span className="text-sm text-gray-600 ml-2">
              Page <strong>{page}</strong> / <strong>{totalPages}</strong>
            </span>
            <div className="text-sm text-gray-600 ml-4">
              <span className="mr-2">Afficher</span>
              <select
                value={limit}
                onChange={(e) => setLimit(Number(e.target.value))}
                className="border px-2 py-1 rounded"
              >
                {[10, 20, 50, 100].map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
              <span className="ml-2">/ page</span>
            </div>
          </div>
          <div className="text-sm text-gray-600">
            Total: <strong>{totalItems}</strong> résultat(s)
          </div>
        </div>
      </div>
    </div>
  );
};

const Stat = ({ label, value, hint, color = 'green' }) => {
  const colorClass =
    {
      green: 'text-green-600',
      blue: 'text-blue-600',
      yellow: 'text-yellow-600',
      red: 'text-red-600',
      purple: 'text-purple-600',
      gray: 'text-gray-700'
    }[color] || 'text-gray-700';

  return (
    <div className="bg-white rounded shadow p-4">
      <p className="text-sm text-gray-500">{label}</p>
      <p className={`text-2xl font-bold ${colorClass}`}>{value}</p>
      {hint ? <p className="text-xs text-gray-400 mt-1">{hint}</p> : null}
    </div>
  );
};

export default AdminPaymentsManagement;
